import React, { Component } from 'react';
import path from 'path-browserify';

import { TreeNodeProps, TreeModel, TreeModelContext } from '../../../../atoms/tree-layer';

import { NewFileNamer } from '../../renamer';
import { Indent } from '../indent';
import { DroppableTreeNode } from '../../../../atoms/dragDrop';

import { FolderTitle, FolderTitleProps } from './folder-title';
import styles from './folder-view.module.scss';

const HOVER_INTERVAL = 480; //MS

export enum AdditionTypes {
	file,
	folder,
}

export type FolderViewProps = {
	onOpenChange?: (isOpen: boolean) => void;
	isOpen: boolean;
	className?: string;
	Title?: React.ComponentType<FolderTitleProps> | null;
} & TreeNodeProps<undefined>;

type FolderViewState = {
	isRenaming: boolean;
	isAdding?: AdditionTypes;
	showDeleteConfirmation: boolean;
};

export class FolderView extends Component<FolderViewProps, FolderViewState> {
	state = {
		isRenaming: false,
		isAdding: undefined,
		showDeleteConfirmation: false,
	};

	static contextType = TreeModelContext;
	context!: TreeModel;

	private handleStartAdd = (type?: AdditionTypes) => {
		this.setState({ isAdding: type });
	};

	private handleSubmitNew = async (newFilename: string) => {
		this.setState({ isAdding: undefined });
		if (!newFilename) return;

		const { node } = this.props;
		const addType = this.state.isAdding;
		const treeModel = this.context;

		const newFileId = path.join(node.id, newFilename);
		if (addType == AdditionTypes.file) {
			await treeModel.writeFile(newFileId);
			treeModel.selectFile(newFileId);
		}
		if (addType == AdditionTypes.folder) {
			return treeModel.mkdir(newFileId);
		}
	};

	private handleAbortNewFile = () => {
		this.setState({ isAdding: undefined });
	};

	private validateNewFilename = (nextName: string) => {
		const treeModel = this.context;
		const { node } = this.props;

		const dirname = node.id;

		return (
			treeModel.isValidFilename(nextName) &&
			treeModel.canRename(path.join(dirname, nextName))
		);
	};

	private open = () => {
		const { onOpenChange } = this.props;
		onOpenChange && onOpenChange(true);
	};

	private handleDrop = (from: string) => {
		const { node } = this.props;
		const { id } = node;

		const treeModel = this.context;

		const basename = path.basename(from);
		const to = path.join(id, basename);

		treeModel.rename(from, to);
		this.open();
	};

	private _hoverDropTimeout?: NodeJS.Timeout = undefined;
	private handleDropHoverIn = () => {
		if (!this._hoverDropTimeout) {
			this._hoverDropTimeout = setTimeout(this.open, HOVER_INTERVAL);
		}
	};
	private handleDropHoverOut = () => {
		if (this._hoverDropTimeout) {
			clearTimeout(this._hoverDropTimeout);
			this._hoverDropTimeout = undefined;
		}
	};

	render() {
		const { node, depth, children, className, Title = FolderTitle } = this.props;
		const { isAdding } = this.state;

		return (
			<DroppableTreeNode
				node={node}
				onDrop={this.handleDrop}
				onDropHoverIn={this.handleDropHoverIn}
				onDropHoverOut={this.handleDropHoverOut}
				className={className}
				classWhenDragged={styles.dropHover}
			>
				{Title && (
					<Title
						node={node}
						isOpen={this.props.isOpen}
						onOpenChange={this.props.onOpenChange}
						depth={depth}
						onStartAdding={this.handleStartAdd}
					/>
				)}

				{/* Ran wants files to be added at the end of file list, otherwise we would not needs complication at all */}
				{/* it's possible move New File logic to TreeModel in the future. set payload to be { type = 'newFileNamer' } */}
				{children}

				{isAdding === AdditionTypes.file && (
					<Indent depth={depth + 1}>
						<NewFileNamer
							onSubmit={this.handleSubmitNew}
							onAbort={this.handleAbortNewFile}
							validate={this.validateNewFilename}
						/>
					</Indent>
				)}

				{isAdding === AdditionTypes.folder && (
					<Indent depth={depth + 1}>
						<NewFileNamer
							onSubmit={this.handleSubmitNew}
							onAbort={this.handleAbortNewFile}
							validate={this.validateNewFilename}
						/>
					</Indent>
				)}
			</DroppableTreeNode>
		);
	}
}
